// This is the main portal
// Created by Maher Salem
// It communicate with the backend and PostgreAQL
//

import axios from "axios";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Bar, Line, Pie } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
);

const API_URL = process.env.REACT_APP_API_SERVER_URL;
const exportToCsvEnabledEmails = [
  "maher.s@oryx.edu.qa",
  "mohammed.n@oryx.edu.qa",
  "iyngaran@oryx.edu.qa",
]; //add more emails as needed

function App() {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0] || null;
  const userName = activeAccount?.name || activeAccount?.username || "";
  const initials =
    userName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase() || "?";

  function handleSignOut() {
    instance.logoutRedirect({ postLogoutRedirectUri: "/" });
  }

  const [editingActivity, setEditingActivity] = useState(null);
  const [viewingActivity, setViewingActivity] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterFaculty, setFilterFaculty] = useState("all");
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: "journal",
    faculty_name: "",
    faculty_email: "",
    department: "",
    title: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [chartType, setChartType] = useState("bar");
  const [chartFilter, setChartFilter] = useState("type");
  const userEmail = (activeAccount?.username || "").toLowerCase();
  const canExportToCsv = exportToCsvEnabledEmails.some(
    (e) => e.toLowerCase() === userEmail,
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [activitiesPerPage, setActivitiesPerPage] = useState(10);

  // Server-side pagination extras
  const [currentPageActivities, setCurrentPageActivities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true); // true on mount so empty-state never flashes
  const tableFiltersRef = useRef({});

  useEffect(() => {
    loadData();
  }, []);

  // Re-fetch the current table page whenever page or page-size changes.
  useEffect(() => {
    fetchActivitiesPage(currentPage, activitiesPerPage, tableFiltersRef.current);
  }, [currentPage, activitiesPerPage]); // eslint-disable-line

  // Get unique faculty names from the current page data
  const uniqueFaculty = useMemo(() => {
    const facultySet = new Set(currentPageActivities.map((a) => a.faculty_name));
    return Array.from(facultySet).sort();
  }, [currentPageActivities]);

  // Server-driven pagination math
  const totalPages = Math.ceil(totalCount / activitiesPerPage);
  const indexOfFirstActivity = (currentPage - 1) * activitiesPerPage;
  const indexOfLastActivity = Math.min(currentPage * activitiesPerPage, totalCount);

  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Acquire MSAL access token silently for the active account.
  const getAccessToken = async () => {
    if (!activeAccount) return null;
    try {
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: ["openid", "profile"],
        account: activeAccount,
      });
      return accessToken;
    } catch {
      return null;
    }
  };

  // Fetch a single page of activities from the server with auth token + optional filters.
  const fetchActivitiesPage = async (page, limit, filters = {}) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = { page, limit };
      if (filters.type && filters.type !== "all") params.type = filters.type;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.faculty && filters.faculty !== "all") params.faculty = filters.faculty;
      if (filters.search) params.search = filters.search;
      const { data } = await axios.get(`${API_URL}/activities`, { headers, params });
      if (data && !Array.isArray(data) && data.data !== undefined) {
        // New paginated format: { data: [...], total: N }
        setCurrentPageActivities(data.data);
        setFilteredActivities(data.data);
        setTotalCount(data.total);
      } else if (Array.isArray(data)) {
        // Fallback: backend returned old flat array
        const slice = data.slice((page - 1) * limit, page * limit);
        setCurrentPageActivities(slice);
        setFilteredActivities(slice);
        setTotalCount(data.length);
      }
    } catch (err) {
      console.error("Error fetching activities page:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      console.log("Loading data...");
      const typesResponse = await axios.get(`${API_URL}/activity-types`);
      setActivityTypes(typesResponse.data);
      const statsResponse = await axios.get(`${API_URL}/statistics`);
      setStats(statsResponse.data);
      // Only fetch the current page — no full-table dump
      await fetchActivitiesPage(currentPage, activitiesPerPage, tableFiltersRef.current);
      console.log("Data loaded successfully!");
    } catch (error) {
      console.error("Error loading data:", error);
      alert(
        "Error loading data. Is the backend running on http://localhost:4000?",
      );
    }
  };

  // --------------------- Function for Logging deleted Activities and show ---------------------------
  // --------------------- a record in the Admin Panel ----------------------------------------------
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [deletionLogs, setDeletionLogs] = useState([]);

  // Add this function to fetch logs
  const fetchDeletionLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/logs/deletions`);
      setDeletionLogs(response.data);
    } catch (error) {
      console.log("Could not fetch deletion logs:", error.message);
      // Fallback to localStorage if backend endpoint doesn't exist
      const localLogs = JSON.parse(
        localStorage.getItem("deletionLogs") || "[]",
      );
      setDeletionLogs(localLogs);
    }
  };

  //-------------------------------- End of logging Function ------------------

  // Apply filters immediately using the latest values (bypasses stale-closure on state).
  const triggerFilter = (overrides = {}) => {
    const f = {
      type:    overrides.type    !== undefined ? overrides.type    : filterType,
      status:  overrides.status  !== undefined ? overrides.status  : filterStatus,
      faculty: overrides.faculty !== undefined ? overrides.faculty : filterFaculty,
      search:  overrides.search  !== undefined ? overrides.search  : searchQuery,
    };
    tableFiltersRef.current = f;
    setCurrentPage(1);
    fetchActivitiesPage(1, activitiesPerPage, f);
  };

  const handleSearch = async () => {
    const searchTerm = prompt("Enter search term:");
    if (searchTerm) {
      try {
        const response = await axios.get(`${API_URL}/search?q=${searchTerm}`);
        setCurrentPageActivities(response.data);
        setFilteredActivities(response.data);
        setTotalCount(response.data.length);
        if (response.data.length === 0) {
          alert("No activities found matching your search.");
        }
      } catch (error) {
        alert("Search failed: " + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingActivity) {
      await handleUpdate(e);
    } else {
      try {
        const submitData = {
          ...formData,
          faculty_name: formData.faculty_name || userName,
          faculty_email: formData.faculty_email || activeAccount?.username || "",
          details: JSON.stringify({
            ...(formData.journal_name && {
              journal_name: formData.journal_name,
            }),
            ...(formData.authors && { authors: formData.authors }),
            ...(formData.doi && { doi: formData.doi }),
            ...(formData.conference_name && {
              conference_name: formData.conference_name,
            }),
            ...(formData.venue && { venue: formData.venue }),
            ...(formData.funding_agency && {
              funding_agency: formData.funding_agency,
            }),
            ...(formData.amount_awarded && {
              amount_awarded: formData.amount_awarded,
            }),
            ...(formData.role && { role: formData.role }),
            ...(formData.organizer && { organizer: formData.organizer }),
            ...(formData.participants_count && {
              participants_count: formData.participants_count,
            }),
            ...(formData.notes && { notes: formData.notes }),
          }),
        };
        await axios.post(`${API_URL}/activities`, submitData);
        setShowForm(false);
        setFormData({
          type: "journal",
          faculty_name: "",
          faculty_email: "",
          department: "",
          title: "",
          date: new Date().toISOString().split("T")[0],
        });
        loadData();
        alert("Activity saved successfully!");
      } catch (error) {
        console.error("FULL ERROR:", error.response?.data || error);
        alert(
          "Error saving activity:\n" +
            (error.response?.data?.error || error.message),
        );
      }
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setFormData({
      type: activity.type,
      faculty_name: activity.faculty_name,
      faculty_email: activity.faculty_email,
      department: activity.department,
      title: activity.title,
      date: activity.date,
      status: activity.status || "Completed",
      ...(activity.details ? JSON.parse(activity.details) : {}),
    });
    setShowForm(true);
  };

  const handleView = (activity) => {
    setViewingActivity(activity);
    setFormData({
      type: activity.type,
      faculty_name: activity.faculty_name,
      faculty_email: activity.faculty_email,
      department: activity.department,
      title: activity.title,
      date: activity.date,
      status: activity.status || "Completed",
      ...(activity.details ? JSON.parse(activity.details) : {}),
    });
    setShowForm(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/activities/${editingActivity.id}`, formData);
      setShowForm(false);
      setEditingActivity(null);
      loadData();
      alert("Activity updated successfully!");
    } catch (error) {
      alert("Error updating activity: " + error.message);
    }
  };

  const handleDelete = async (id) => {
    // Find the activity being deleted for logging purposes
    const activityToDelete = activities.find((a) => a.id === id);

    if (
      window.confirm(
        "Are you sure you want to delete this activity?\n\n⚠️ This action will be logged and the administrator will be notified.",
      )
    ) {
      try {
        // Create log data before deletion
        const logData = {
          activity_id: id,
          activity_title: activityToDelete?.title || "Unknown",
          activity_type:
            getActivityTypeName(activityToDelete?.type) || "Unknown",
          faculty_name: activityToDelete?.faculty_name || "Unknown",
          deleted_by: "User", // In a real app, this would come from user authentication
          deleted_at: new Date().toISOString(),
          reason: "Manual deletion via interface",
        };

        // Send delete request
        await axios.delete(`${API_URL}/activities/${id}`);

        // Log the deletion (send to backend for storage)
        try {
          await axios.post(`${API_URL}/logs`, {
            type: "activity_deletion",
            message: `Activity "${activityToDelete?.title}" deleted by user`,
            details: logData,
            severity: "warning",
          });
        } catch (logError) {
          console.warn("Failed to log deletion:", logError);
          // Continue even if logging fails
        }

        // Notify admin (send notification to backend)
        try {
          await axios.post(`${API_URL}/notifications`, {
            type: "activity_deleted",
            title: "Activity Deleted",
            message: `Activity "${activityToDelete?.title}" (${getActivityTypeName(activityToDelete?.type)}) has been deleted.`,
            recipient: "admin", // Or specific admin email
            metadata: logData,
          });
        } catch (notifyError) {
          console.warn("Failed to send admin notification:", notifyError);
          // Continue even if notification fails
        }

        // Refresh data and show success message
        loadData();

        // Enhanced success message
        alert(
          `✅ Activity deleted successfully!\n\n📋 Deletion details:\n• Title: ${activityToDelete?.title || "Unknown"}\n• Type: ${getActivityTypeName(activityToDelete?.type) || "Unknown"}\n• Faculty: ${activityToDelete?.faculty_name || "Unknown"}\n\n📝 A deletion record has been logged.\n👮 The administrator has been notified of this action.`,
        );
      } catch (error) {
        alert("❌ Error deleting activity: " + error.message);
      }
    }
  };

  const handleExport = async () => {
    if (!canExportToCsv) return;
    try {
      const response = await fetch(`${API_URL}/export`);

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const filename = `research_activities_${new Date().toISOString().split("T")[0]}.csv`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      alert("CSV export started!");
    } catch (error) {
      console.error("Export error:", error);
      alert(`Export failed: ${error.message}`);
    }
  };

  // Example of how to attach this to a button
  // <button id="export-button" onClick={handleExport}>Export to CSV</button>

  const generatePDFReport = () => {
    const doc = new jsPDF();
    autoTable(doc, {});

    doc.setFontSize(20);
    doc.setTextColor(30, 60, 114);
    doc.text("Research Activities Report", 105, 20, null, null, "center");

    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Generated on ${format(new Date(), "MMMM dd, yyyy")}`,
      105,
      30,
      null,
      null,
      "center",
    );

    doc.setFontSize(14);
    doc.setTextColor(30, 60, 114);
    doc.text("Summary Statistics", 20, 45);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Activities: ${stats?.total ?? 0}`, 20, 55);
    doc.text(`Journal Articles: ${stats?.byType?.journal ?? 0}`, 20, 62);
    doc.text(`Conference Papers: ${stats?.byType?.conference ?? 0}`, 20, 69);
    doc.text(`Research Grants: ${stats?.byType?.grant ?? 0}`, 20, 76);

    doc.setFontSize(14);
    doc.setTextColor(30, 60, 114);
    doc.text("Activity Type Distribution", 20, 90);

    const activityCounts = currentPageActivities.reduce((acc, activity) => {
      const typeName = getActivityTypeName(activity.type);
      acc[typeName] = (acc[typeName] || 0) + 1;
      return acc;
    }, {});

    const distributionData = Object.entries(activityCounts).map(
      ([type, count]) => [type, count],
    );

    autoTable(doc, {
      startY: 95,
      head: [["Activity Type", "Count"]],
      body: distributionData,
      theme: "striped",
      headStyles: { fillColor: [30, 60, 114] },
    });

    doc.setFontSize(14);
    doc.setTextColor(30, 60, 114);
    doc.text("Detailed Activities", 20, doc.lastAutoTable.finalY + 10);

    const tableData = currentPageActivities.map((activity) => [
      getActivityTypeName(activity.type),
      activity.title,
      activity.faculty_name,
      activity.department,
      activity.date,
      activity.status || "Not Specified",
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 15,
      head: [["Type", "Title", "Faculty", "Department", "Date", "Status"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: [30, 60, 114] },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 40 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
      },
      margin: { left: 10, right: 10 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, 195, 285, null, null, "right");
    }

    doc.save(`research_report_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  const getActivityTypeName = (typeId) => {
    const type = activityTypes.find((t) => t.id === typeId);
    return type ? type.name : typeId;
  };

  const prepareChartData = () => {
    const dataMap = {};
    filteredActivities.forEach((activity) => {
      let key;
      if (chartFilter === "type") {
        key = getActivityTypeName(activity.type);
      } else if (chartFilter === "department") {
        key = activity.department || "Unknown";
      } else if (chartFilter === "status") {
        key = activity.status || "Not Specified";
      }
      if (key) {
        dataMap[key] = (dataMap[key] || 0) + 1;
      }
    });
    return Object.entries(dataMap)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 15) + "..." : name,
        value,
        fullName: name,
      }))
      .sort((a, b) => b.value - a.value);
  };

  const chartData = prepareChartData();
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884D8",
    "#82CA9D",
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
  ];

  const getChartJSData = () => {
    const data = prepareChartData();
    const chartData = {
      labels: data.map((item) => item.name),
      datasets: [
        {
          label: chartType === "bar" ? "Number of Activities" : "",
          data: data.map((item) => item.value),
          backgroundColor: COLORS,
          borderColor: "#ffffff",
          borderWidth: 1,
        },
      ],
    };
    return chartData;
  };

  // ===== VISUAL ANALYTICS DATA DASHBOARD =====

  // Bar Chart – Activities by Type
  const barChartData = {
    labels: activityTypes.map((t) => t.name),
    datasets: [
      {
        label: "Activities",
        data: activityTypes.map(
          (t) => filteredActivities.filter((a) => a.type === t.id).length,
        ),
        backgroundColor: COLORS,
      },
    ],
  };

  // Pie Chart – Activities by Status
  const statusCounts = filteredActivities.reduce((acc, a) => {
    const status = a.status || "Not Specified";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const pieChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        data: Object.values(statusCounts),
        backgroundColor: COLORS,
      },
    ],
  };

  // Line Chart – Activities Over Time (Monthly)
  const monthlyCounts = filteredActivities.reduce((acc, a) => {
    const month = format(new Date(a.date), "yyyy-MM");
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  const lineChartData = {
    labels: Object.keys(monthlyCounts),
    datasets: [
      {
        label: "Activities Over Time",
        data: Object.values(monthlyCounts),
        borderColor: "#3182ce",
        backgroundColor: "rgba(49,130,206,0.2)",
        tension: 0.4,
      },
    ],
  };

  const chartJSOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: chartType !== "bar",
        position: "right",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.raw || 0;
            return `${label}: ${value} activities`;
          },
        },
      },
    },
    scales:
      chartType === "bar"
        ? {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
              },
            },
          }
        : undefined,
  };

  const PaginationControls = () => {
    const visiblePages = (() => {
      const delta = 2;
      const range = [];
      for (
        let i = Math.max(1, currentPage - delta);
        i <= Math.min(totalPages, currentPage + delta);
        i++
      ) {
        range.push(i);
      }
      return range;
    })();

    return (
      <div style={styles.paginationContainer}>
        {/* Left: rows-per-page selector */}
        <div style={styles.pageSizeSelector}>
          <span style={styles.pageSizeLabel}>Rows per page:</span>
          <select
            style={styles.pageSizeSelect}
            value={activitiesPerPage}
            onChange={(e) => {
              setActivitiesPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        {/* Right: page info + navigation */}
        <div style={styles.paginationRight}>
          <span style={styles.paginationInfo}>
            {totalCount === 0 ? "0" : indexOfFirstActivity + 1}–{indexOfLastActivity} of {totalCount}
          </span>

          <div style={styles.paginationButtons}>
            <button
              style={{
                ...styles.pageButton,
                opacity: currentPage === 1 ? 0.4 : 1,
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              ‹ Prev
            </button>

            {visiblePages[0] > 1 && (
              <>
                <button style={styles.pageNumberButton} onClick={() => setCurrentPage(1)}>1</button>
                {visiblePages[0] > 2 && <span style={styles.pageEllipsis}>…</span>}
              </>
            )}

            {visiblePages.map((n) => (
              <button
                key={n}
                style={{
                  ...styles.pageNumberButton,
                  ...(currentPage === n ? styles.activePageButton : {}),
                }}
                onClick={() => setCurrentPage(n)}
              >
                {n}
              </button>
            ))}

            {visiblePages[visiblePages.length - 1] < totalPages && (
              <>
                {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                  <span style={styles.pageEllipsis}>…</span>
                )}
                <button style={styles.pageNumberButton} onClick={() => setCurrentPage(totalPages)}>
                  {totalPages}
                </button>
              </>
            )}

            <button
              style={{
                ...styles.pageButton,
                opacity: currentPage === totalPages ? 0.4 : 1,
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
    );
  };

  //------------------------- The following can be used later for visulization -----------------------

  // Data for Activities over Time (Line Chart) ---
  const activitiesOverTimeData = useMemo(() => {
    const activitiesByMonth = filteredActivities.reduce((acc, activity) => {
      const date = new Date(activity.date);
      const monthYear = format(date, "MMM yyyy");
      acc[monthYear] = (acc[monthYear] || 0) + 1;
      return acc;
    }, {});

    const sortedMonthYears = Object.keys(activitiesByMonth).sort((a, b) => {
      return new Date(a) - new Date(b);
    });

    return {
      labels: sortedMonthYears,
      datasets: [
        {
          label: "Activities over Time",
          data: sortedMonthYears.map(
            (monthYear) => activitiesByMonth[monthYear],
          ),
          borderColor: "#8884D8",
          backgroundColor: "rgba(136, 132, 216, 0.5)",
          tension: 0.3,
        },
      ],
    };
  }, [filteredActivities]);

  // Data for Activities by Faculty (Bar Chart) ---
  const activitiesByFacultyData = useMemo(() => {
    const activitiesByFaculty = filteredActivities.reduce((acc, activity) => {
      const facultyName = activity.faculty_name || "Unknown";
      acc[facultyName] = (acc[facultyName] || 0) + 1;
      return acc;
    }, {});

    const sortedFaculty = Object.entries(activitiesByFaculty)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, 10); // Top 10 faculty

    return {
      labels: sortedFaculty.map(([name]) => name),
      datasets: [
        {
          label: "Activities by Faculty (Top 10)",
          data: sortedFaculty.map(([, count]) => count),
          backgroundColor: COLORS,
        },
      ],
    };
  }, [filteredActivities]);

  // Data for Activity Status (Pie Chart) ---
  const activitiesByStatusData = useMemo(() => {
    const activitiesByStatus = filteredActivities.reduce((acc, activity) => {
      const status = activity.status || "Not Specified";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(activitiesByStatus),
      datasets: [
        {
          label: "Activity Status Distribution",
          data: Object.values(activitiesByStatus),
          backgroundColor: COLORS,
          borderColor: "#ffffff",
          borderWidth: 1,
        },
      ],
    };
  }, [filteredActivities]);

  // -------------------------------------- End Here -----------------------------------

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: "20px",
            width: "100%",
          }}
        >
          {/* Left side content */}
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <svg
              width="60"
              height="60"
              viewBox="0 0 90 90"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ cursor: "pointer", transition: "transform 0.3s" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.05)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            >
              {/* SVG format */}
              <style>
                {`
				@keyframes pulse {
				  0%, 100% { opacity: 0.8; }
				  50% { opacity: 1; }
				}
				@keyframes float {
				  0%, 100% { transform: translateY(0); }
				  50% { transform: translateY(-3px); }
				}
				.pulse { animation: pulse 2s infinite; }
				.float { animation: float 3s infinite ease-in-out; }
				.rotate { animation: rotate 20s linear infinite; }
				@keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
			  `}
              </style>

              <defs>
                <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop
                    offset="0%"
                    style={{ stopColor: "#4F46E5", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#7C3AED", stopOpacity: 1 }}
                  />
                </linearGradient>
                <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop
                    offset="0%"
                    style={{ stopColor: "#10B981", stopOpacity: 0.8 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#0EA5E9", stopOpacity: 0.8 }}
                  />
                </linearGradient>
              </defs>

              <circle
                cx="45"
                cy="45"
                r="38"
                stroke="url(#grad1)"
                strokeWidth="2"
                strokeDasharray="4,4"
                opacity="0.6"
                className="rotate"
              />
              <path
                d="M45 25C40 25 35 28 32 33C30 36 29 40 30 44C31 48 34 51 38 53C42 55 47 55 51 53C55 51 58 48 59 44C60 40 59 36 57 33C54 28 49 25 45 25Z"
                fill="url(#grad2)"
                className="pulse"
              />
              <circle
                cx="45"
                cy="15"
                r="3"
                fill="#F59E0B"
                opacity="0.8"
                className="float"
              />
              <circle
                cx="30"
                cy="30"
                r="2"
                fill="#8B5CF6"
                opacity="0.7"
                className="pulse"
              />
              <circle
                cx="60"
                cy="30"
                r="2"
                fill="#10B981"
                opacity="0.7"
                className="pulse"
                style={{ animationDelay: "0.5s" }}
              />
              <circle cx="40" cy="60" r="2" fill="#EF4444" opacity="0.7" />
              <circle cx="50" cy="60" r="2" fill="#0EA5E9" opacity="0.7" />
              <circle cx="45" cy="45" r="5" fill="#FFFFFF" className="pulse" />
            </svg>

            <div>
              <h1 style={styles.title}>
                Research Activity Management System (RAMS)
              </h1>
              <p style={styles.subtitle}>
                Track and manage scholarly and Academic activities
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              justifySelf: "flex-end",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "rgba(255,255,255,0.7)",
                fontSize: "0.78rem",
                paddingRight: "16px",
                borderRight: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <span
                style={{
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </span>
              <button
                onClick={handleSignOut}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.6)",
                  padding: "4px 10px",
                  borderRadius: 4,
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  fontWeight: 600,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.15)";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                }}
              >
                Sign out
              </button>
            </div>
            <div
              style={{
                backgroundColor: "white",
                padding: "10px 15px",
                borderRadius: "8px",
              }}
            >
              <img
                src={
                  require("./icons/oryxlogo.png").default ||
                  require("./icons/oryxlogo.png")
                }
                alt="Logo"
                style={{
                  width: "250px",
                  height: "auto",
                  maxHeight: "150px",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
        </div>
      </header>

      {!showForm && (
        <div style={styles.dashboard}>
          {/* Statistics Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>
                <img
                  src={
                    require("./icons/totalactivities.png").default ||
                    require("./icons/totalactivities.png")
                  }
                  alt="Total Activities"
                  style={{ width: "32px", height: "32px" }}
                />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>{stats?.total ?? 0}</div>
                <div style={styles.statLabel}>Total Activities</div>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statIcon}>
                <img
                  src={
                    require("./icons/journal.png").default ||
                    require("./icons/journal.png")
                  }
                  alt="Journals"
                  style={{ width: "32px", height: "32px" }}
                />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>
                  {stats?.byType?.journal ?? 0}
                </div>
                <div style={styles.statLabel}>Journal Articles</div>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statIcon}>
                <img
                  src={
                    require("./icons/conference.png").default ||
                    require("./icons/conference.png")
                  }
                  alt="Conference"
                  style={{ width: "32px", height: "32px" }}
                />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>
                  {stats?.byType?.conference ?? 0}
                </div>
                <div style={styles.statLabel}>Conference Papers</div>
              </div>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statIcon}>
                <img
                  src={
                    require("./icons/grants.png").default ||
                    require("./icons/grants.png")
                  }
                  alt="Grants"
                  style={{ width: "32px", height: "32px" }}
                />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>
                  {stats?.byType?.grant ?? 0}
                </div>
                <div style={styles.statLabel}>Research Grants</div>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div style={styles.filterSection}>
            <h3 style={styles.filterTitle}>Filter Activities</h3>
            <div style={styles.filters}>
              <select
                style={styles.filterSelect}
                value={filterType}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilterType(v);
                  triggerFilter({ type: v });
                }}
              >
                <option value="all">All Types</option>
                {activityTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>

              <select
                style={styles.filterSelect}
                value={filterStatus}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilterStatus(v);
                  triggerFilter({ status: v });
                }}
              >
                <option value="all">All Status</option>
                <option value="Completed">Completed</option>
                <option value="In Progress">In Progress</option>
                <option value="Planned">Planned</option>
                <option value="Published">Published</option>
                <option value="Submitted">Submitted</option>
              </select>

              <select
                style={styles.filterSelect}
                value={filterFaculty}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilterFaculty(v);
                  triggerFilter({ faculty: v });
                }}
              >
                <option value="all">All Faculty</option>
                {uniqueFaculty.map((faculty) => (
                  <option key={faculty} value={faculty}>
                    {faculty}
                  </option>
                ))}
              </select>

              <input
                style={styles.filterInput}
                type="text"
                placeholder="Search by title or faculty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") triggerFilter({ search: e.target.value });
                }}
              />

              <button
                style={styles.filterButton}
                onClick={() => triggerFilter()}
              >
                Apply Filters
              </button>

              <button
                style={styles.clearFilterButton}
                onClick={() => {
                  setFilterType("all");
                  setFilterStatus("all");
                  setFilterFaculty("all");
                  setSearchQuery("");
                  tableFiltersRef.current = {};
                  setCurrentPage(1);
                  fetchActivitiesPage(1, activitiesPerPage, {});
                }}
              >
                Clear Filters
              </button>

              {/* Top Activity Element */}
              {(() => {
                // Calculate most frequent activity type
                const activityCounts = currentPageActivities.reduce((acc, activity) => {
                  const typeName = getActivityTypeName(activity.type);
                  acc[typeName] = (acc[typeName] || 0) + 1;
                  return acc;
                }, {});

                const topActivity = Object.entries(activityCounts).reduce(
                  (max, [type, count]) =>
                    count > max.count ? { type, count } : max,
                  { type: "None", count: 0 },
                );

                if (topActivity.count > 0) {
                  return (
                    <div style={styles.topActivityContainer}>
                      <div style={styles.topActivityBadge}>
                        <span style={styles.topActivityIcon}>🏆</span>
                        <span style={styles.topActivityText}>
                          Top Activity: <strong>{topActivity.type}</strong> (
                          {topActivity.count})
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={styles.content}>
        {/* Left Sidebar */}
        <div style={styles.sidebar}>
          <button
            style={styles.addButton}
            onClick={() => {
              setFormData({
                type: "journal",
                faculty_name: userName,
                faculty_email: activeAccount?.username || "",
                department: "",
                title: "",
                date: new Date().toISOString().split("T")[0],
              });
              setShowForm(true);
            }}
          >
            ➕ Add New Activity
          </button>

          <div style={styles.sidebarSection}>
            <h3 style={styles.sidebarTitle}>Activity Types</h3>
            <div style={styles.typeList}>
              <button
                style={styles.typeButton}
                onClick={() => {
                  tableFiltersRef.current = {};
                  setFilterType("all");
                  setFilterStatus("all");
                  setSearchQuery("");
                  setCurrentPage(1);
                  fetchActivitiesPage(1, activitiesPerPage, {});
                }}
              >
                All Activities ({totalCount})
              </button>
              {activityTypes.map((type) => (
                <button
                  key={type.id}
                  style={styles.typeButton}
                  onClick={() => {
                    const f = { type: type.id };
                    tableFiltersRef.current = f;
                    setFilterType(type.id);
                    setCurrentPage(1);
                    fetchActivitiesPage(1, activitiesPerPage, f);
                  }}
                >
                  {type.name} ({stats?.byType[type.id] || 0})
                </button>
              ))}
            </div>
          </div>

          <div style={styles.sidebarSection}>
            <h3 style={styles.sidebarTitle}>Quick Actions</h3>
            <button style={styles.actionButton} onClick={loadData}>
              🔄 Refresh Data
            </button>
            {canExportToCsv && (
              <button style={styles.actionButton} onClick={handleExport}>
                📥 Export to CSV
              </button>
            )}
            <button style={styles.actionButton} onClick={generatePDFReport}>
              📄 Generate PDF Report
            </button>
          </div>
        </div>

        {/* Main Area */}
        <div style={styles.main}>
          {/* Activity Form - Shows when form is open */}
          {showForm && (
            <div style={styles.formContainer}>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>
                  {viewingActivity ? "View Activity" : editingActivity ? "Edit Activity" : "Add New Activity"}
                </h2>
                <button
                  style={styles.closeButton}
                  onClick={() => {
                    setShowForm(false);
                    setEditingActivity(null);
                    setViewingActivity(null);
                    setFormData({
                      type: "journal",
                      faculty_name: "",
                      faculty_email: "",
                      department: "",
                      title: "",
                      date: new Date().toISOString().split("T")[0],
                    });
                  }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} style={styles.form}>
                {/* Basic Info Section */}
                <div style={styles.formSection}>
                  <h3 style={styles.sectionTitle}>Basic Information</h3>
                  <div style={styles.formGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Activity Type:</label>
                      <select
                        style={styles.select}
                        value={formData.type}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            type: e.target.value,
                            title: "",
                            date: new Date().toISOString().split("T")[0],
                          });
                        }}
                        required
                        disabled={!!viewingActivity}
                      >
                        <option value="">Select Activity Type</option>
                        {activityTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ ...styles.formGroup, gridColumn: "1 / span 2" }}>
                      <label style={styles.label}>Submitted By</label>
                      <div style={styles.facultyCard}>
                        <div style={styles.facultyAvatar}>
                          {viewingActivity || editingActivity
                            ? (formData.faculty_name || "").split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() || "?"
                            : initials}
                        </div>
                        <div style={styles.facultyCardInfo}>
                          <span style={styles.facultyCardName}>
                            {viewingActivity || editingActivity ? formData.faculty_name : userName}
                          </span>
                          <span style={styles.facultyCardEmail}>
                            {viewingActivity || editingActivity ? formData.faculty_email : activeAccount?.username || ""}
                          </span>
                        </div>
                        <span style={styles.autoFetchBadge}>
                          🔒 Auto-fetched from account
                        </span>
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Department:</label>
                      <input
                        style={styles.input}
                        type="text"
                        value={formData.department}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            department: e.target.value,
                          })
                        }
                        required
                        placeholder="e.g., Computer Science"
                        disabled={!!viewingActivity}
                      />
                    </div>
                  </div>
                </div>

                {/* Type-Specific Fields */}
                {formData.type && (
                  <div style={styles.formSection}>
                    <h3 style={styles.sectionTitle}>
                      {activityTypes.find((t) => t.id === formData.type)
                        ?.name || "Activity"}{" "}
                      Details
                    </h3>

                    <div style={styles.formGrid}>
                      {/* Title Field */}
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Title:</label>
                        <input
                          style={styles.input}
                          type="text"
                          value={formData.title}
                          onChange={(e) =>
                            setFormData({ ...formData, title: e.target.value })
                          }
                          required
                          placeholder="Enter activity title"
                          disabled={!!viewingActivity}
                        />
                      </div>

                      {/* Date Field */}
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Date:</label>
                        <input
                          style={styles.input}
                          type="date"
                          value={formData.date}
                          onChange={(e) =>
                            setFormData({ ...formData, date: e.target.value })
                          }
                          required
                          disabled={!!viewingActivity}
                        />
                      </div>

                      {/* Additional Fields based on activity type */}
                      {formData.type === "journal" && (
                        <>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Journal Name:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.journal_name || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  journal_name: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Authors:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.authors || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  authors: e.target.value,
                                })
                              }
                              required
                              placeholder="Comma-separated names"
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>DOI:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.doi || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  doi: e.target.value,
                                })
                              }
                              placeholder="e.g., 10.xxxx/xxxxx"
                              disabled={!!viewingActivity}
                            />
                          </div>
                        </>
                      )}

                      {formData.type === "conference" && (
                        <>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Conference Name:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.conference_name || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  conference_name: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Venue:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.venue || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  venue: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>
                              Presentation Type:
                            </label>
                            <select
                              style={styles.select}
                              value={formData.presentation_type || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  presentation_type: e.target.value,
                                })
                              }
                              disabled={!!viewingActivity}
                            >
                              <option value="">Select Type</option>
                              <option value="Oral">Oral Presentation</option>
                              <option value="Poster">
                                Poster Presentation
                              </option>
                              <option value="Keynote">Keynote Speech</option>
                              <option value="Invited Talk">Invited Talk</option>
                            </select>
                          </div>
                        </>
                      )}

                      {formData.type === "grant" && (
                        <>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Funding Agency:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.funding_agency || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  funding_agency: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Amount Awarded:</label>
                            <input
                              style={styles.input}
                              type="number"
                              value={formData.amount_awarded || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  amount_awarded: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Your Role:</label>
                            <select
                              style={styles.select}
                              value={formData.role || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  role: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            >
                              <option value="">Select Role</option>
                              <option value="Principal Investigator">
                                Principal Investigator
                              </option>
                              <option value="Co-Investigator">
                                Co-Investigator
                              </option>
                              <option value="Collaborator">Collaborator</option>
                              <option value="Consultant">Consultant</option>
                            </select>
                          </div>
                        </>
                      )}

                      {formData.type === "workshop" && (
                        <>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Workshop Title:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.title || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  title: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Organizer:</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={formData.organizer || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  organizer: e.target.value,
                                })
                              }
                              required
                              disabled={!!viewingActivity}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>
                              Number of Participants:
                            </label>
                            <input
                              style={styles.input}
                              type="number"
                              value={formData.participants_count || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  participants_count: e.target.value,
                                })
                              }
                              disabled={!!viewingActivity}
                            />
                          </div>
                        </>
                      )}

                      {/* Status field for all types */}
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Status:</label>
                        <select
                          style={styles.select}
                          value={formData.status || "Completed"}
                          onChange={(e) =>
                            setFormData({ ...formData, status: e.target.value })
                          }
                          disabled={!!viewingActivity}
                        >
                          <option value="Planned">Planned</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Published">Published</option>
                          <option value="Submitted">Submitted</option>
                        </select>
                      </div>

                      {/* Description/Notes field */}
                      <div
                        style={{
                          ...styles.formGroup,
                          gridColumn: "1 / span 2",
                        }}
                      >
                        <label style={styles.label}>Additional Notes:</label>
                        <textarea
                          style={styles.textarea}
                          value={formData.notes || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, notes: e.target.value })
                          }
                          placeholder="Any additional details..."
                          rows={3}
                          disabled={!!viewingActivity}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div style={styles.formButtons}>
                  <button
                    type="button"
                    style={styles.cancelButton}
                    onClick={() => {
                      setShowForm(false);
                      setEditingActivity(null);
                      setViewingActivity(null);
                      setFormData({
                        type: "journal",
                        faculty_name: "",
                        faculty_email: "",
                        department: "",
                        title: "",
                        date: new Date().toISOString().split("T")[0],
                      });
                    }}
                  >
                    Go Back
                  </button>
                  {!viewingActivity && (
                    <button type="submit" style={styles.submitButton}>
                      {editingActivity ? "Update Activity" : "Save Activity"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Activities List - Shows when form is NOT open */}
          {!showForm && (
            <div style={styles.activitiesContainer}>
              <h2 style={styles.sectionTitle}>
                Activities ({totalCount})
                <button style={styles.smallButton} onClick={loadData}>
                  Refresh
                </button>
              </h2>

              {totalCount === 0 && !isLoading ? (
                <div style={styles.emptyState}>
                  <p>No activities found. Try changing your filters.</p>
                  <button
                    style={styles.addButton}
                    onClick={() => setShowForm(true)}
                  >
                    Add Your First Activity
                  </button>
                </div>
              ) : (
                <>
                <PaginationControls />
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Title</th>
                        <th style={styles.th}>Faculty</th>
                        <th style={styles.th}>Department</th>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPageActivities.map((activity) => (
                        <tr key={activity.id} style={styles.tr}>
                          <td style={styles.td}>
                            <span style={styles.typeBadge}>
                              {getActivityTypeName(activity.type)}
                            </span>
                          </td>
                          <td style={styles.td}>{activity.title}</td>
                          <td style={styles.td}>{activity.faculty_name}</td>
                          <td style={styles.td}>{activity.department}</td>
                          <td style={styles.td}>{activity.date}</td>
                          <td style={styles.td}>
                            <span
                              style={{
                                ...styles.statusBadge,
                                backgroundColor:
                                  activity.status === "Completed"
                                    ? "#c6f6d5"
                                    : activity.status === "In Progress"
                                      ? "#fed7d7"
                                      : "#e2e8f0",
                                color:
                                  activity.status === "Completed"
                                    ? "#22543d"
                                    : activity.status === "In Progress"
                                      ? "#742a2a"
                                      : "#4a5568",
                              }}
                            >
                              {activity.status || "Not Specified"}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actionButtons}>
                              <button
                                style={styles.viewButton}
                                onClick={() => handleView(activity)}
                                title="View"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                  <circle cx="12" cy="12" r="3"/>
                                </svg>
                              </button>
                              {(
                                activity.faculty_email?.toLowerCase() ===
                                  activeAccount?.username?.toLowerCase() ||
                                (!activity.faculty_email &&
                                  activity.faculty_name === userName)
                              ) && (
                                <>
                                  <button
                                    style={styles.editButton}
                                    onClick={() => handleEdit(activity)}
                                    title="Edit"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                    </svg>
                                  </button>
                                  <button
                                    style={styles.deleteButton}
                                    onClick={() => handleDelete(activity.id)}
                                    title="Delete"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"/>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          )}

          {/* Visual Analytics Dashboard */}
          {!showForm && (
            <div style={styles.activitiesContainer}>
              <h2 style={styles.sectionTitle}>
                <div style={styles.statIcon}>
                  <img
                    src={
                      require("./icons/dashboard.png").default ||
                      require("./icons/dashboard.png")
                    }
                    alt="Main Dashboard"
                    style={{ width: "32px", height: "32px" }}
                  />
                </div>{" "}
                Research Analytics Dashboard
              </h2>

              <div style={styles.chartsGrid}>
                {/* Bar Chart */}
                <div style={styles.chartCard}>
                  <h3 style={styles.chartTitleSmall}>Activities by Type</h3>
                  <div style={styles.chartCanvas}>
                    <Bar
                      data={barChartData}
                      options={{ maintainAspectRatio: false }}
                    />
                  </div>
                </div>

                {/* Pie Chart */}
                <div style={styles.chartCard}>
                  <h3 style={styles.chartTitleSmall}>Activities by Status</h3>
                  <div style={styles.chartCanvas}>
                    <Pie
                      data={pieChartData}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: "bottom",
                            labels: {
                              boxWidth: 10,
                              font: { size: 10 },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Line Chart */}
                <div style={styles.chartCard}>
                  <h3 style={styles.chartTitleSmall}>Activities Over Time</h3>
                  <div style={styles.chartCanvas}>
                    <Line
                      data={lineChartData}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: true,
                            position: "bottom",
                            labels: {
                              font: { size: 10 },
                            },
                          },
                        },
                        scales: {
                          x: {
                            ticks: {
                              maxRotation: 45,
                              minRotation: 45,
                              font: { size: 10 },
                            },
                          },
                          y: {
                            beginAtZero: true,
                            ticks: {
                              stepSize: 1,
                              font: { size: 10 },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      <footer style={styles.footer}>
        <p>
          Research Activity Management System | Data is saved to PostgreSQL
          database
        </p>
        <p>
          Copy Right: Research and Knowledge Exchange Office at Oryx University
        </p>
      </footer>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
    fontSize: "14px",
  },
  header: {
    background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    color: "white",
    padding: "20px 25px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  title: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "700",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: "6px 0 0 0",
    opacity: 0.9,
    fontSize: "14px",
    fontWeight: "400",
  },
  dashboard: {
    padding: "20px 25px",
    backgroundColor: "white",
    margin: "15px",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
  },
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },

  chartCard: {
    backgroundColor: "white",
    padding: "15px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },

  chartCanvas: {
    height: "250px",
    position: "relative",
  },

  chartTitleSmall: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#2d3748",
    marginBottom: "10px",
    textAlign: "center",
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "15px",
    backgroundColor: "white",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    transition: "all 0.3s",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  },
  statIcon: {
    fontSize: "24px",
    background: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
    color: "white",
    width: "50px",
    height: "50px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statContent: {
    flex: 1,
  },
  statNumber: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2d3748",
    marginBottom: "3px",
  },
  statLabel: {
    fontSize: "12px",
    color: "#718096",
    fontWeight: "500",
  },
  chartSection: {
    margin: "30px 0",
    backgroundColor: "#f8fafc",
    padding: "25px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "15px",
  },
  chartTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#2d3748",
    margin: 0,
  },
  chartControls: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  chartSelect: {
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    backgroundColor: "white",
    fontSize: "13px",
    color: "#4a5568",
    minWidth: "120px",
    outline: "none",
  },
  chartTypeButton: {
    padding: "8px 16px",
    backgroundColor: "#e2e8f0",
    color: "#4a5568",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    transition: "all 0.2s",
  },
  pdfButton: {
    padding: "8px 16px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  // --- NEW: Grid layout for multiple charts ---
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    gap: "20px",
    marginBottom: "20px",
  },
  chartContainer: {
    height: "350px",
    width: "100%",
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "15px",
    border: "1px solid #e2e8f0",
  },
  chartSubtitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#4a5568",
    marginBottom: "15px",
    textAlign: "center",
  },
  noChartData: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    color: "#a0aec0",
    fontSize: "14px",
  },
  chartSummary: {
    display: "flex",
    gap: "20px",
    backgroundColor: "white",
    padding: "15px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px 15px",
    backgroundColor: "#f8fafc",
    borderRadius: "6px",
    minWidth: "120px",
  },
  summaryLabel: {
    fontSize: "12px",
    color: "#718096",
    fontWeight: "500",
    marginBottom: "5px",
  },
  summaryValue: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#2d3748",
  },
  filterSection: {
    marginTop: "20px",
    marginBottom: "25px",
  },
  filterTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2d3748",
    marginBottom: "12px",
  },

  // Add these to your styles object:
  topActivityContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "10px",
    padding: "8px 12px",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeaa7",
    borderRadius: "6px",
    fontSize: "12px",
    color: "#856404",
  },

  topActivityBadge: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  topActivityIcon: {
    fontSize: "14px",
  },

  topActivityText: {
    fontWeight: "500",
  },

  filters: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  filterSelect: {
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    backgroundColor: "white",
    fontSize: "13px",
    color: "#4a5568",
    minWidth: "150px",
    outline: "none",
    transition: "all 0.2s",
  },
  filterInput: {
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    color: "#4a5568",
    minWidth: "200px",
    outline: "none",
    transition: "all 0.2s",
  },
  filterButton: {
    padding: "8px 16px",
    backgroundColor: "#4299e1",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    transition: "all 0.2s",
  },
  clearFilterButton: {
    padding: "8px 16px",
    backgroundColor: "#e2e8f0",
    color: "#4a5568",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    transition: "all 0.2s",
  },
  content: {
    display: "flex",
    minHeight: "500px",
  },
  sidebar: {
    width: "250px",
    backgroundColor: "white",
    borderRight: "1px solid #e2e8f0",
    padding: "20px",
    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.04)",
  },
  addButton: {
    width: "100%",
    padding: "12px",
    background: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    marginBottom: "20px",
    transition: "all 0.2s",
    boxShadow: "0 2px 4px rgba(66, 153, 225, 0.3)",
  },
  sidebarSection: {
    marginBottom: "25px",
  },
  sidebarTitle: {
    fontSize: "12px",
    textTransform: "uppercase",
    color: "#a0aec0",
    marginBottom: "12px",
    fontWeight: "600",
    letterSpacing: "0.5px",
  },
  typeList: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  typeButton: {
    textAlign: "left",
    padding: "8px 12px",
    backgroundColor: "transparent",
    color: "#4a5568",
    border: "none",
    cursor: "pointer",
    borderRadius: "5px",
    fontSize: "13px",
    fontWeight: "500",
    transition: "all 0.2s",
  },
  actionButton: {
    width: "100%",
    padding: "10px 12px",
    backgroundColor: "#f7fafc",
    color: "#4a5568",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    cursor: "pointer",
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: "500",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  main: {
    flex: 1,
    padding: "20px 25px",
    backgroundColor: "#f8fafc",
  },
  viewmain: {
    flex: 1,
    padding: "20px 25px",
    backgroundColor: "#f8fafc",
  },
  formContainer: {
    backgroundColor: "white",
    padding: "25px",
    borderRadius: "10px",
    marginBottom: "20px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e2e8f0",
  },
  formHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  formTitle: {
    margin: 0,
    color: "#2d3748",
    fontSize: "20px",
    fontWeight: "600",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "18px",
    color: "#a0aec0",
    cursor: "pointer",
    padding: "5px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formSection: {
    backgroundColor: "#f8fafc",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "15px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
  },
  input: {
    padding: "10px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    color: "#2d3748",
    backgroundColor: "white",
    transition: "all 0.2s",
    outline: "none",
  },
  readOnlyInput: {
    padding: "10px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    color: "#2d3748",
    backgroundColor: "white",
    transition: "all 0.2s",
    outline: "none",
  },
  select: {
    padding: "10px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    color: "#2d3748",
    backgroundColor: "white",
    transition: "all 0.2s",
    outline: "none",
  },
  textarea: {
    padding: "10px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    color: "#2d3748",
    backgroundColor: "white",
    transition: "all 0.2s",
    outline: "none",
    resize: "vertical",
    minHeight: "80px",
    fontFamily: "inherit",
  },
  formButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e2e8f0",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#e2e8f0",
    color: "#4a5568",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    transition: "all 0.2s",
  },
  submitButton: {
    padding: "10px 24px",
    background: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    transition: "all 0.2s",
    boxShadow: "0 2px 4px rgba(66, 153, 225, 0.3)",
  },
  activitiesContainer: {
    backgroundColor: "white",
    padding: "25px",
    borderRadius: "10px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e2e8f0",
  },
  sectionTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "0 0 25px 0",
    color: "#2d3748",
    fontSize: "18px",
    fontWeight: "600",
  },
  smallButton: {
    padding: "6px 12px",
    backgroundColor: "#4299e1",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
    transition: "all 0.2s",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 30px",
    color: "#a0aec0",
    backgroundColor: "#f8fafc",
    borderRadius: "8px",
    border: "2px dashed #e2e8f0",
    fontSize: "13px",
  },
  tableContainer: {
    overflowX: "auto",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px",
  },
  th: {
    padding: "12px 15px",
    textAlign: "left",
    backgroundColor: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tr: {
    borderBottom: "1px solid #e2e8f0",
    transition: "background-color 0.2s",
  },
  td: {
    padding: "12px 15px",
    verticalAlign: "middle",
    fontSize: "13px",
    color: "#2d3748",
  },
  typeBadge: {
    display: "inline-block",
    padding: "4px 8px",
    backgroundColor: "#ebf8ff",
    color: "#3182ce",
    borderRadius: "15px",
    fontSize: "11px",
    fontWeight: "600",
    border: "1px solid #bee3f8",
  },
  statusBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
  },
  viewButton: {
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ebf8ff",
    color: "#3182ce",
    border: "1px solid #bee3f8",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    padding: 0,
  },
  editButton: {
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0fff4",
    color: "#276749",
    border: "1px solid #9ae6b4",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    padding: 0,
  },
  deleteButton: {
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff5f5",
    color: "#c53030",
    border: "1px solid #fc8181",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    padding: 0,
  },
  chartCard: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  footer: {
    marginTop: "25px",
    padding: "15px",
    backgroundColor: "#2d3748",
    color: "#cbd5e0",
    textAlign: "center",
    fontSize: "12px",
    borderTop: "1px solid #4a5568",
  },

  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #e2e8f0",
    marginBottom: "15px",
    flexWrap: "wrap",
    gap: "10px",
  },

  paginationRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  paginationInfo: {
    fontSize: "13px",
    color: "#718096",
    fontWeight: "500",
    whiteSpace: "nowrap",
  },

  paginationButtons: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  pageEllipsis: {
    padding: "0 4px",
    color: "#a0aec0",
    fontSize: "13px",
  },

  pageButton: {
    padding: "8px 12px",
    backgroundColor: "#edf2f7",
    color: "#4a5568",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
    transition: "all 0.2s",
    minWidth: "80px",
  },

  pageNumbers: {
    display: "flex",
    gap: "5px",
  },

  pageNumberButton: {
    padding: "6px 10px",
    backgroundColor: "white",
    color: "#4a5568",
    border: "1px solid #e2e8f0",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
    minWidth: "36px",
    transition: "all 0.2s",
  },

  activePageButton: {
    backgroundColor: "#4299e1",
    color: "white",
    borderColor: "#4299e1",
  },

  pageSizeSelector: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  pageSizeLabel: {
    fontSize: "13px",
    color: "#718096",
  },

  pageSizeSelect: {
    padding: "6px 10px",
    border: "1px solid #e2e8f0",
    borderRadius: "4px",
    backgroundColor: "white",
    fontSize: "12px",
    color: "#4a5568",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "28px",
    width: "600px",
    maxWidth: "90vw",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e2e8f0",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  modalField: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    backgroundColor: "#f8fafc",
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  modalLabel: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  facultyCard: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "12px 16px",
    backgroundColor: "#eef2ff",
    border: "1px solid #c7d2fe",
    borderRadius: "8px",
  },
  facultyAvatar: {
    width: "40px",
    height: "40px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "700",
    fontSize: "14px",
    flexShrink: 0,
  },
  facultyCardInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flex: 1,
  },
  facultyCardName: {
    fontWeight: "600",
    fontSize: "14px",
    color: "#1e3a8a",
  },
  facultyCardEmail: {
    fontSize: "12px",
    color: "#4338ca",
  },
  autoFetchBadge: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#4338ca",
    backgroundColor: "#c7d2fe",
    padding: "4px 10px",
    borderRadius: "12px",
    whiteSpace: "nowrap",
  },
};

export default App;
